import React, { useCallback } from 'react';

import { Spin } from 'antd';
import classnames from 'classnames';

import LocationsMap from '@src/components/LocationsMap';
import IntlMessages from '@src/util/IntlMessages';

import { useTimelineEvent } from './hooks/useTimelineEvent';
import type { TimelineEventProps } from './TimelineEvent.types';
import styles from './index.module.less';

const TimelineEvent: React.FC<TimelineEventProps> = ({
  event,
  onOpen,
  selected = false,
  railVariant = 'icon',
  connectorTone = 'success',
  checkpointMapHeight,
}) => {
  const {
    isCheckpointRoute,
    iconColor,
    actorName,
    targetName,
    markerLocationName,
    contextSegments,
    progressLabel,
    dateTimeLabel,
    triggerLoading,
    eventIcon,
    checkpointRouteMapItem,
  } = useTimelineEvent(event);

  const handleOpen = useCallback(() => {
    onOpen?.(event);
  }, [event, onOpen]);

  const handleKeyDown = useCallback(
    (keyboardEvent: React.KeyboardEvent<HTMLDivElement>) => {
      if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
        keyboardEvent.preventDefault();
        handleOpen();
      }
    },
    [handleOpen],
  );

  const useStatusRail = railVariant === 'status';
  const hasContext = contextSegments.length > 0 || Boolean(progressLabel) || triggerLoading;

  return (
    <div
      className={classnames(
        styles.item,
        onOpen && styles.itemClickable,
        selected && styles.itemSelected,
      )}
      data-testid="timeline-event"
      aria-current={selected ? 'true' : undefined}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen ? handleOpen : undefined}
      onKeyDown={onOpen ? handleKeyDown : undefined}
    >
      <div className={styles.rail}>
        <div
          className={classnames(styles.badge, useStatusRail && styles.badgeStatus)}
          style={useStatusRail ? undefined : { backgroundColor: iconColor }}
          data-testid={useStatusRail ? 'timeline-event-status-badge' : undefined}
        >
          {!useStatusRail &&
            (isCheckpointRoute ? (
              <span className="material-symbols-outlined">route</span>
            ) : (
              <i className="material-icons-outlined">{eventIcon}</i>
            ))}
        </div>
        <div
          className={classnames(
            styles.connector,
            useStatusRail && styles.connectorStatus,
            useStatusRail && connectorTone === 'pending' && styles.connectorPending,
          )}
          aria-hidden
        />
      </div>

      <div className={styles.body}>
        <span className={styles.dateTime}>{dateTimeLabel}</span>

        <div className={styles.titleLine}>
          {actorName && <span className={styles.actor}>{actorName}</span>}
          <span className={styles.action}>
            <IntlMessages
              id={
                isCheckpointRoute
                  ? 'timeline.event.checkpointMarkedAction'
                  : 'timeline.event.completedAction'
              }
            />
          </span>
          {targetName && <span className={styles.target}>{`“${targetName}”`}</span>}
          {markerLocationName && (
            <span className={styles.action}>
              <IntlMessages
                id="timeline.event.atMarker"
                values={{
                  name: <span className={styles.target}>{markerLocationName}</span>,
                }}
              />
            </span>
          )}
        </div>

        <span className={classnames('material-icons', styles.chevron)} aria-hidden>
          keyboard_arrow_right
        </span>

        {hasContext && (
          <div className={styles.contextLine}>
            {contextSegments.map((segment, index) => (
              <React.Fragment key={`${segment}-${index}`}>
                {index > 0 && <span className={styles.contextSeparator}>/</span>}
                <span className={styles.contextSegment}>{segment}</span>
              </React.Fragment>
            ))}
            {progressLabel && (
              <>
                <span className={classnames('material-icons', styles.contextArrow)}>
                  keyboard_arrow_right
                </span>
                <span className={styles.contextSegment}>{progressLabel}</span>
              </>
            )}
            {triggerLoading && <Spin size="small" />}
          </div>
        )}

        {checkpointRouteMapItem && (
          <LocationsMap
            visible
            interactive={false}
            height={checkpointMapHeight ?? '160px'}
            mapMinHeight={checkpointMapHeight}
            className={styles.checkpointRouteMap}
            mapClassName={styles.checkpointRouteMapInner}
            items={[checkpointRouteMapItem]}
            getPopupText={item => item?.name}
            popupTrigger="hover"
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(TimelineEvent);
